import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  Requisition,
  RequisitionData,
  RequisitionFilters,
  RequisitionResponse,
  UsersWho
} from '../shared/models/requisition.model';
// import { baseURL } from './config';
import { environment } from '../../../environments/environment';
import { handleApiResponse } from '../utils/common-methods';
import { AlertMessageService } from './alert-message.service';
import { UserService } from './user.service';
import { ErrorResponse } from '../shared/models/general-responses.model';

export interface RequisitionsItem {
  componentResourceId: number;
  quantity: number;
  productionUnitId?: number;
  notes?: string
  itemId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RequisitionService {
  private headers = new HttpHeaders();

  constructor(private http: HttpClient, private userService: UserService, private alertService: AlertMessageService) {}

  public requisitionTabs = [
    'Todas',
    'Iniciado',
    'En espera de revisión',
    'En espera de autorizar',
    'Enviado a compras',
    'En proceso de compra',
    'Comprada',
    'En proceso de entrega',
    'Vencida',
    'Entregada',
    'Rechazada',
    'Cancelada',
  ];

  /**
   * Fetches the latest requisitions to authorize for a given project and user.
   *
   * @param projectId - The ID of the project or 'all' to fetch requisitions for all projects.
   * @param userId - The ID of the user.
   * @returns An Observable that emits an array of Requisition objects.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/dashboards/requisition_table` endpoint
   * with the specified project ID and user ID. It handles the response by mapping the data
   * to an array of Requisition objects if the status is 'SUCCESS'. If an error occurs, it logs
   * the error and returns an empty array.
   *
   * @example
   * ```typescript
   * this.requisitionService.getLatestToAuthorizeRequisitions(1, 123).subscribe((requisitions) => {
   *   console.log(requisitions);
   * });
   * ```
   */
  public getLatestToAuthorizeRequisitions(
    projectId: number | 'all',
    userId: number
  ): Observable<Requisition[]> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/dashboards/requisition_table/${projectId}?page=1&limit=30`;

    return this.http.get<RequisitionResponse>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return (handleApiResponse(response, this.alertService) as RequisitionData).rows ?? [];
      }),
      catchError((error) => {
        console.error('Error fetching requisitions:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of([] as Requisition[]);
      })
    );
  }

  public getUserInRequisitions(ProjectID: string): Observable<UsersWho | null> {
    const URL = `${environment.baseURL}/requisition_users/${ProjectID}`;

    return this.http
      .get<RequisitionResponse>(URL, { headers: this.headers })
      .pipe(
        map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as UsersWho ?? null;
      }),
      catchError((error) => {
        console.error('Error fetching users in requisitions:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of(null);
      })
    );
  }

  /**
   * Fetches all the requisitions for a given project.
   *
   * @param projectId - The ID of the project or 'all' to fetch requisitions for all projects.
   * @returns An Observable that emits an array of Requisition objects.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/requisitions` endpoint
   * with the specified project ID. It handles the response by mapping the data
   * to an array of Requisition objects if the status is 'SUCCESS'. If an error occurs, it logs
   * the error and returns an empty array.
   *
   * @example
   * ```typescript
   * this.requisitionService.getRequisitionsListByProjectId(1).subscribe((requisitions) => {
   *   console.log(requisitions);
   * });
   * ```
   */
  getRequisitionsListByProjectId(
    projectId: number | 'all',
    page: number,
    limit: number,
    order: string,
    filters: string[]
  ): Observable<RequisitionData> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisitions?project_id=${projectId}&page=${page}&limit=${limit}`
    +`${
      order === undefined || order === null || order === ''
        ? ''
        : '&order=' + order
    }` +
      `${
        filters.length > 0
          ? `${filters.map((x) => `&filters=${x}`).join('&')}`
          : ''
      }`;
    return this.http.get<RequisitionResponse>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as RequisitionData ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching requisitions by project:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as RequisitionData);
      })
    );
  }

  /**
   * Fetches all the users in requisitions for a given project.
   *
   * @param projectId - The ID of the project or 'all' to fetch requisitions for all projects.
   * @returns An Observable that emits an array of Requisition Users objects.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/requisition_users/:ProjectID` endpoint
   * with the specified project ID. It handles the response by mapping the data
   * to an array of Requisition objects if the status is 'SUCCESS'. If an error occurs, it logs
   * the error and returns an empty array.
   *
   * @example
   * ```typescript
   * this.requisitionService.getRequisitionsUsersByProjectId(1).subscribe((requisitionData) => {
   *   console.log(requisitionData);
   * });
   * ```
   */
  getRequisitionsUsersByProjectId(
    projectId: number | 'all'
  ): Observable<UsersWho> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisition_users/${projectId}`;
    return this.http.get<RequisitionResponse>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as UsersWho ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching users in requisitions by project:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as UsersWho);
      })
    );
  }

  /**
   * Gets the requisition filters for a project
   * @param projectId - The ID of the project.
   * @returns An Observable that emits the requisition filters.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/filter-info/requisitions` endpoint
   * with the specified project ID. It handles the response by mapping the data
   * to an array of RequisitionFilters object if the status is 'SUCCESS'. If an error occurs, it logs
   * the error and returns an empty object.
   *
   * @example
   * ```typescript
   * this.requisitionService.getRequisitionFilters(1).subscribe((requisitionFilters) => {
   *   console.log(requisitionFilters);
   * });
   * ```
   */
  getRequisitionFilters(projectId: number | 'all'): Observable<any> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/filter-info/requisitions?project_id=${projectId}`;
    return this.http.get<RequisitionResponse>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as RequisitionFilters ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching requisition filters:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as RequisitionFilters);
      })
    );
  }

  /**
   * Gets a requisition by ID
   * @param id - The ID of the requisition to get.
   * @returns An Observable that emits the requisition.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/requisitions/:id` endpoint
   * with the specified ID. It handles the response by mapping the data
   * to a requisition object if the status is 'SUCCESS'. If an error occurs, it logs
   * the error and returns an empty object.
   *
   * @example
   * ```typescript
   * this.requisitionService.getRequisitionById(1).subscribe((requisition) => {
   *   console.log(requisition);
   * });
   * ```
   */
  getRequisitionById(id: number): Observable<Requisition> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisitions/${id}`;
    return this.http.get<RequisitionResponse>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as Requisition ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching requisition:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as Requisition);
      })
    );
  }

  /**
   * Fetches the requisition items for a given requisition ID.
   *
   * @param {number} requisitionId - The unique identifier of the requisition to fetch items for.
   * @param grouped
   * @return {Observable<any>} An observable that emits the requisition items if successful, or `false` in case of an error.
   */
  public getRequisitionItems(requisitionId: number, grouped?: boolean): Observable<any> {
    const headers = this.userService.headers;
    let URL = `${environment.baseURL}/requisitions/${requisitionId}`;
    if (grouped) {
      URL += `?items=itemsGroupedByResource`;
    } else {
      URL += `?items=standard`;
    }

    return this.http.get<any>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as RequisitionData ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching requisition items:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as RequisitionData);
      })
    );
  }

  /**
   * Updates a requisition
   *
   * @param requisition - The requisition to update.
   * @returns An Observable that emits the updated requisition.
   *
   * @remarks
   * This method makes an HTTP GET request to the `/api/requisition_users/:requisitionId` endpoint
   * with the updated requisition. It handles the response by mapping the data
   * to an requisition object if the status is 'SUCCESS'. If an error occurs, it logs to
   * the error and returns an empty object.
   *
   * @example
   * ```typescript
   * this.requisitionService.updateRequisition({requisition object}).subscribe((requisition) => {
   *   console.log(requisition);
   * });
   * ```
   */
  updateRequisition(
    requisition: Partial<Requisition>
  ): Observable<Requisition | ErrorResponse> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisitions/${requisition.requisitionId}`;
    return this.http
      .put<RequisitionResponse>(URL, requisition, { headers })
      .pipe(
        map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as Requisition | ErrorResponse ?? {};
      }),
      catchError((error) => {
        console.error('Error updating requisition:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as Requisition);
      })
    );
  }

  /**
   * Deletes a requisition by its ID.
   * @param id  The ID of the requisition to delete.
   * @returns An Observable that confirms the deletion.
   * @remarks
   * This method makes an HTTP DELETE request to the `/api/requisitions/:id` endpoint
   * with the specified ID. It confirms the deletion if the status is 'SUCCESS'. 
   * If an error occurs, it logs the error and returns an empty object.
   *
   * @example
   * ```typescript
   * this.requisitionService.deleteRequisition(1).subscribe((response) => {
   *   console.log(response);
   * });
   * ```
   */
  deleteRequisition(id: number) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisitions/${id}`;
    return this.http.delete<any>(URL, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService);
      }),
      catchError((error) => {
        console.error('Error deleting requisition:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({});
      })
    );
  }

  public postAddRequisition(data = {}): Observable<any> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/requisitions`;
    return this.http.post<any>(URL, data, { headers }).pipe(
      map((response: RequisitionResponse) => {
        return handleApiResponse(response, this.alertService) as Requisition ?? {};
      }),
      catchError((error) => {
        console.error('Error adding requisition:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as Requisition);
      })
    );
  }

  exportRequisitionInPDF(requisitionID: number, reportID: string) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/export/requisitions/${requisitionID}/items/${reportID}`;

    return this.http.get(URL, { headers, responseType: 'blob' } as const).pipe(
      map((pdfBlob: Blob) => {
        if (pdfBlob.type === 'application/pdf') {
          return window.URL.createObjectURL(pdfBlob);
        } else {
          return this.blobToJson(pdfBlob).then((jsonData) => {
            return jsonData;
          });
        }

      })
    );
  }

  private blobToJson(blob: Blob): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          const jsonData = JSON.parse(reader.result as string);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }


  /**
   * Performs create, update, or delete (CUD) operations on items linked to a requisition.
   *
   * @param {number} id - The identifier of the requisition with which the items are associated or the item ID.
   * @param {'add' | 'edit' | 'delete'} mod - Specifies the operation mode: 'add' to add items, 'edit' to update items, or 'delete' to remove items.
   * @param {RequisitionsItem[] | { quantity: number }} [item] - An optional parameter specifying the items to be added or updated. Only required for 'add' or 'edit' operations.
   * @return {Observable<{ status: boolean; message: string }>} Observable containing the operation status and a message indicating success or error details.
   */
  public CUDItemToRequisition(
    id: number,
    mod: 'add' | 'edit' | 'delete',
    item?: RequisitionsItem[] | { quantity?: number; notes?: string } | { notes: string; }
  ): Observable<{ status: boolean; message: string }> {
    const headers = this.userService.headers;
    const url = `${environment.baseURL}/requisitions_items/${id}`;
    let httpObservable: Observable<any>;

    if (mod === 'add') {
      httpObservable = this.http.post<any>(url, item, { headers });
    } else if (mod === 'edit') {
      httpObservable = this.http.put<any>(url, item, { headers });
    } else { // 'delete'
      httpObservable = this.http.delete<any>(url, { headers });
    }

    return httpObservable.pipe(
       map((response: any) => {
         if (response && !response.error['has-error']) {
           if (mod === 'add') {
             return { status: true, message: 'Se agregaron los items a la requisición' }
           } else if (mod === 'edit') {
             return { status: true, message: 'Se ha actualizado la requisición' };
           } else {
             return { status: true, message: 'Se eliminaron los items de la requisición' };
           }
         } else {
           return { status: false, message: response? response.error.error : 'No información relativa al error' };
         }
       }),
      catchError((error) => {
          console.error('Error adding item to requisition:', error);
          this.alertService.updateAlert(error.error!);
          this.alertService.changeErrorState(true);
          return of({ status: false, message: "Error en la transacción"});
        })
      )
  }

 public exportRequisitionsByProjectId(projectId: number | 'all') {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/export/requisitions?project_id=${projectId}`;

    return this.http.get(URL, { headers, responseType: 'blob' } as const).pipe(
      map((blob: Blob) => {
        if (blob.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          return window.URL.createObjectURL(blob);
        } else {
          return this.blobToJson(blob).then((jsonData) => {
            return jsonData;
          });
        }
      })
    );
  }

  public exportSelectedRequisitionsByProjectId(projectId: number | 'all', requisitionIds: number[]) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/export/requisitions?project_id=${projectId}&ids=${requisitionIds.join(',')}`;

    return this.http.get(URL, { headers, responseType: 'blob' } as const).pipe(
      map((blob: Blob) => {
        if (blob.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          return window.URL.createObjectURL(blob);
        } else {
          return this.blobToJson(blob).then((jsonData) => {
            return jsonData;
          });
        }
      })
    );
  }
}
